import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { Form, Input, FormInstance, Col, theme, Row, Skeleton, Divider } from 'antd';
import { MessageInstance } from 'antd/es/message/interface';

import { useStores } from '@/hooks/use-stores';
import { useResponsive } from '@/hooks/useResponsive';
import { DeviceType, compressorTypes } from '@/constants/deviceTypeEnum';
import { EPSelect, EPSelectOption } from '@/components';

import { Entity } from '../../../NodeWidget.types';
import {
  getDeprecatedModelToolTip,
  getFieldSelectProps,
  IsDeprecatedModel,
  IsDisconnectedDevice,
  useFormHelper,
} from '../../EditAssetDrawer.utils';
import {
  defaultDeviceModels,
  DRYER_WIDGET,
  getDryerTypeOptions,
  mapFormValuesToPayload,
  useInitialValues,
} from './EditDryerForm.utils';
import { EditDryerFormContainer } from './EditDryerForm.styles';
import ConnectionSelector from '../ConnectionSelector/ConnectionSelector';
import ConnectionList from '../ConnectionList/ConnectionList';

const EditDryerForm = observer(
  ({
    entity,
    form,
    afterOpenChange,
    messageApi,
  }: {
    entity: Entity;
    form: FormInstance;
    afterOpenChange: boolean;
    messageApi: MessageInstance;
  }) => {
    const { t } = useTranslation();
    const { pidEditStore } = useStores();
    const { isMobile } = useResponsive();
    const { token } = theme.useToken();

    const [currentDeviceType, setCurrentDeviceType] = useState(entity.deviceType);
    const deprecatedModel = IsDeprecatedModel(entity.deviceKind);

    // Watch the form's deviceType field to keep state in sync
    const formDeviceType = Form.useWatch('deviceType', form);
    const effectiveDeviceType = formDeviceType || currentDeviceType;
    const Widget = DRYER_WIDGET[effectiveDeviceType];

    const { deviceModelOptions, isLoadingModels, onFinish, onModelSearch, initialValues, deviceConnectionOptions } =
      useFormHelper(
        t,
        entity,
        afterOpenChange,
        messageApi,
        mapFormValuesToPayload,
        useInitialValues,
        effectiveDeviceType,
      );
    const currentModelId = Form.useWatch('deviceModelId', form);
    const disconnectedDevice = IsDisconnectedDevice(deviceModelOptions, currentModelId);

    const isLoading = !afterOpenChange || pidEditStore.isLoadingDraft;

    const handleDeviceTypeChange = newDeviceType => {
      setCurrentDeviceType(newDeviceType);
      form.setFieldValue('deviceModelId', defaultDeviceModels[newDeviceType]);
    };

    if (isLoading) {
      return <Skeleton active paragraph={{ rows: 6 }} />;
    }

    return (
      <EditDryerFormContainer>
        <div style={{ display: isLoading ? 'none' : 'block' }}>
          <Form layout="vertical" form={form} initialValues={initialValues} onFinish={onFinish}>
            <span className="horizontal-label">{t('connect')}</span>
            <Row className="connected-to-row">
              <Col span={6}>
                <Form.Item label=" " name="connectFrom">
                  <ConnectionSelector
                    label="from"
                    options={deviceConnectionOptions.filter(option => option.deviceType !== DeviceType.ProductionFloor)}
                    form={form}
                  />
                </Form.Item>
              </Col>
              <Col span={4}>
                <div className="pipe" />
              </Col>
              <Col span={4} className="widget">
                <Widget width={68} height={82} color={token.colorBorder} />
              </Col>
              <Col span={4}>
                <div className="pipe" />
              </Col>
              <Col span={6}>
                <Form.Item label=" " name="connectTo">
                  <ConnectionSelector
                    label="to"
                    options={deviceConnectionOptions.filter(option => !compressorTypes.includes(option.deviceType))}
                    form={form}
                  />
                </Form.Item>
              </Col>
            </Row>
            <ConnectionList form={form} field="from" />
            <ConnectionList form={form} field="to" />
            <Divider className="divider" />
            <Form.Item label={t('name')} name="name">
              <Input size="large" />
            </Form.Item>

            <Form.Item label={t('type')} name="deviceType">
              <EPSelect
                {...getFieldSelectProps(isMobile, form, t('selectType'), 'deviceType')}
                onChange={handleDeviceTypeChange}
              >
                {getDryerTypeOptions(t).map(option => (
                  <EPSelectOption key={option.value} value={option.value}>
                    {option.label}
                  </EPSelectOption>
                ))}
              </EPSelect>
            </Form.Item>

            <Form.Item
              label={t('modelSpec')}
              name="deviceModelId"
              tooltip={deprecatedModel ? getDeprecatedModelToolTip() : undefined}
              required
              rules={[{ required: true, message: t('required') }]}
            >
              <EPSelect
                {...getFieldSelectProps(isMobile, form, t('selectModelSpec'), 'deviceModelId')}
                loading={isLoadingModels}
                onSearch={onModelSearch}
                filterOption={false}
                disabled={deprecatedModel}
              >
                {deviceModelOptions.map(option => (
                  <EPSelectOption key={option.value} value={option.value}>
                    {option.label}
                  </EPSelectOption>
                ))}
              </EPSelect>
            </Form.Item>

            <Form.Item label={t('controller')} name="controllerId" rules={[{ required: !disconnectedDevice, message: t('required') }]}>
              <EPSelect
                {...getFieldSelectProps(isMobile, form, t('selectController'), 'controllerId')}
                disabled={disconnectedDevice}
              >
                {pidEditStore.deviceControllers.map(option => (
                  <EPSelectOption key={option.value} value={option.value}>
                    {option.label}
                  </EPSelectOption>
                ))}
              </EPSelect>
            </Form.Item>
          </Form>
        </div>
      </EditDryerFormContainer>
    );
  },
);
export default EditDryerForm;
